import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * U1: Unified button component with brand-consistent variants
 *
 * Provides consistent styling across the application while supporting
 * the design system's primary/secondary patterns.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Brand variants matching btn-primary/btn-secondary CSS classes
        brand:
          "bg-terracotta-500 text-white hover:bg-terracotta-600 shadow-sm transition-all duration-200 hover:shadow-md",
        "brand-secondary":
          "bg-white text-espresso-800 border border-cream-300 hover:bg-cream-50 hover:border-cream-400 shadow-sm transition-all duration-200",
        // Success/warning variants for form feedback
        success:
          "bg-sage-500 text-white hover:bg-sage-600 shadow-sm",
        warning:
          "bg-gold-500 text-espresso-900 hover:bg-gold-600 shadow-sm",
      },
      size: {
        xs: "h-7 rounded px-2 text-xs",
        sm: "h-9 rounded-md px-3 text-sm",
        default: "h-10 px-4 py-2 text-base",
        lg: "h-11 rounded-md px-8 text-lg",
        xl: "h-12 rounded-lg px-10 text-lg",
        icon: "h-10 w-10 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }